import { useLocalParticipant, useRoomContext } from '@livekit/components-react'
import { useCallback, useEffect, useState } from 'react'

const VIRTUAL_AUDIO_DEVICES = ['blackhole', 'soundflower', 'loopback']

function isVirtualAudioDevice(device: MediaDeviceInfo): boolean {
  const label = device.label.toLowerCase()
  return VIRTUAL_AUDIO_DEVICES.some((name) => label.includes(name))
}

function useDeviceList() {
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([])
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([])

  const enumerate = useCallback(async () => {
    const devices = await navigator.mediaDevices.enumerateDevices()
    setAudioDevices(devices.filter((d) => d.kind === 'audioinput' && !isVirtualAudioDevice(d)))
    setVideoDevices(devices.filter((d) => d.kind === 'videoinput'))
  }, [])

  useEffect(() => {
    enumerate()
    navigator.mediaDevices.addEventListener('devicechange', enumerate)
    return () => navigator.mediaDevices.removeEventListener('devicechange', enumerate)
  }, [enumerate])

  return { audioDevices, videoDevices }
}

function getDeviceId(track: ReturnType<typeof useLocalParticipant>['microphoneTrack']): string {
  return track?.track?.mediaStreamTrack?.getSettings().deviceId ?? ''
}

export function useRoomDevices() {
  const room = useRoomContext()
  const { microphoneTrack, cameraTrack } = useLocalParticipant()
  const { audioDevices, videoDevices } = useDeviceList()
  const [selectedAudioDevice, setSelectedAudioDevice] = useState('')
  const [selectedVideoDevice, setSelectedVideoDevice] = useState('')

  useEffect(() => {
    const id = getDeviceId(microphoneTrack)
    if (id) setSelectedAudioDevice(id)
  }, [microphoneTrack])

  useEffect(() => {
    const id = getDeviceId(cameraTrack)
    if (id) setSelectedVideoDevice(id)
  }, [cameraTrack])

  const switchAudioDevice = useCallback(
    async (deviceId: string) => {
      setSelectedAudioDevice(deviceId)
      await room.switchActiveDevice('audioinput', deviceId)
    },
    [room]
  )

  const switchVideoDevice = useCallback(
    async (deviceId: string) => {
      setSelectedVideoDevice(deviceId)
      await room.switchActiveDevice('videoinput', deviceId)
    },
    [room]
  )

  return {
    audioDevices,
    videoDevices,
    selectedAudioDevice,
    selectedVideoDevice,
    switchAudioDevice,
    switchVideoDevice,
  }
}
